"use client"

import {useEffect, useRef} from "react";
import {useAppSelector} from "@/lib/hooks";
import {isFcmConfigured} from "@/lib/firebase/config";
import {getOrchestrator} from "@/lib/dialog-orchestrator";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {safeSessionStorage} from "@/lib/utils/storage";
import {AUTH_SESSION_ID_KEY, PUSH_PROMPT_SHOWN_SESSION_ID_KEY} from "@/lib/utils/contants/welcomeBonusStorage";

/**
 * Headless post-login manager for the web-push soft prompt — AC-649.
 *
 * It owns *eligibility* (FCM configured, permission still 'default', logged in)
 * and asks the orchestrator for the {@link PushPermissionPrompt} toast. The
 * orchestrator gives it the lowest priority and admits it only while nothing
 * else is on screen: a modal (e.g. the post-registration WELCOME_BONUS_PROMO
 * drawer) disables pointer events outside itself, so a toast under it can't be
 * tapped.
 *
 * Shown once per login session: once it has been on screen, reloads skip it
 * (the session id lives in sessionStorage and is reset by `AuthInitializer` on
 * login/logout) — the next login shows it again. The permanent on/off control
 * lives in the avatar menu.
 */
// Settling delay: lets page-level dialogs requested right after auth (e.g.
// WELCOME_BONUS_PROMO at 1.5 s) enter the active set first.
const SETTLE_DELAY_MS = 3_000;

function canPrompt(userId: string | null | undefined): boolean {
    return isFcmConfigured() &&
        typeof window !== "undefined" &&
        typeof Notification !== "undefined" &&
        !!userId &&
        Notification.permission === "default";
}

function shownThisSession(): boolean {
    const sessionId = safeSessionStorage.getItem(AUTH_SESSION_ID_KEY);
    return !!sessionId && safeSessionStorage.getItem(PUSH_PROMPT_SHOWN_SESSION_ID_KEY) === sessionId;
}

function markShownThisSession(): void {
    const sessionId = safeSessionStorage.getItem(AUTH_SESSION_ID_KEY);
    if (sessionId) safeSessionStorage.setItem(PUSH_PROMPT_SHOWN_SESSION_ID_KEY, sessionId);
}

export default function EnablePushNotificationsPrompt() {
    const userId = useAppSelector((state) => state.auth.userId);

    const userIdRef = useRef(userId);
    // Live request (queued or visible) in the orchestrator.
    const instanceIdRef = useRef<string | null>(null);
    // The user already answered or closed the toast during this login.
    const handledForUserRef = useRef<string | null | undefined>(null);

    useEffect(() => {
        userIdRef.current = userId;
        const orchestrator = getOrchestrator();

        // `requeue` = the toast was already shown on this page and got closed by
        // the system (yield, route change); it comes back regardless of the
        // session mark, which only stops a *reload* from showing it again.
        const request = (requeue = false) => {
            const uid = userIdRef.current;
            if (instanceIdRef.current || !canPrompt(uid) || handledForUserRef.current === uid) return;
            if (!requeue && shownThisSession()) return;

            const {instanceId, result} = orchestrator.open(DialogsEnum.PUSH_PERMISSION, {
                source: "system",
                delay: SETTLE_DELAY_MS,
            });
            instanceIdRef.current = instanceId;

            void result.then((res) => {
                if (instanceIdRef.current !== res.instanceId) return;
                instanceIdRef.current = null;
                if (res.success || res.reason === "user") {
                    handledForUserRef.current = uid;
                    return;
                }
                // Closed by the system (yielded to another dialog, route change,
                // replaced) — queue it again until the screen is clear.
                request(true);
            });
        };

        if (!userId) {
            if (instanceIdRef.current) orchestrator.cancel(instanceIdRef.current);
            instanceIdRef.current = null;
            return;
        }

        request();

        // Yield: `canShow` only gates admission, so a dialog opening while the
        // toast is already up would cover it. Close it; the result handler re-queues.
        const offOpened = orchestrator.on("opened", (event) => {
            if (event.id === DialogsEnum.PUSH_PERMISSION) {
                markShownThisSession();
                return;
            }
            const instanceId = instanceIdRef.current;
            if (instanceId && orchestrator.isActive(instanceId)) {
                orchestrator.close(instanceId, {success: false, reason: "programmatic"});
            }
        });

        // Legacy Radix dialogs opened directly by pages are invisible to the engine
        // (the config's `canShow` DOM-probes them); re-evaluate the queue when their
        // portals mount/unmount so a waiting request is admitted once they close.
        const observer = new MutationObserver(() => {
            if (instanceIdRef.current && !orchestrator.isActive(instanceIdRef.current)) {
                orchestrator.notifyContextChanged();
            }
        });
        observer.observe(document.body, {childList: true});

        return () => {
            offOpened();
            observer.disconnect();
        };
    }, [userId]);

    return null;
}
